/**
 * SQLite compatibility utilities.
 *
 * SQLite stores booleans as 0/1 (INTEGER).
 * This module handles the conversion to true/false.
 *
 * Each Knex instance gets its own scoped registry via createSqliteBooleanProcessor().
 * The module-level exports (registerBooleanColumn, sqlitePostProcess) use a shared
 * default instance for backward compatibility.
 */

import { extractTableFromSelect } from './sql-utils.js'

export interface SqliteBooleanProcessor {
  registerBooleanColumn(table: string, column: string): void
  convertBooleans(table: string, row: Record<string, unknown>): Record<string, unknown>
  postProcess(result: unknown, queryContext: { sql?: string }): unknown
  clear(): void
}

/**
 * Creates a scoped SQLite boolean processor with its own registry.
 * Use this for per-connection isolation (avoids test interference).
 */
export function createSqliteBooleanProcessor(): SqliteBooleanProcessor {
  const booleanColumns = new Map<string, Set<string>>()

  function registerBooleanColumn(table: string, column: string): void {
    if (!booleanColumns.has(table)) {
      booleanColumns.set(table, new Set())
    }
    booleanColumns.get(table)!.add(column)
  }

  function convertBooleans(
    table: string,
    row: Record<string, unknown>
  ): Record<string, unknown> {
    const columns = booleanColumns.get(table)
    if (!columns || columns.size === 0) return row

    const result = { ...row }
    for (const column of columns) {
      if (column in result) {
        const value = result[column]
        if (value === 0 || value === 1) {
          result[column] = value === 1
        }
      }
    }
    return result
  }

  function postProcess(result: unknown, queryContext: { sql?: string }): unknown {
    const sql = queryContext?.sql?.toLowerCase() ?? ''
    if (!sql.startsWith('select')) return result

    const table = extractTableFromSelect(sql)
    if (!table) return result

    if (Array.isArray(result)) {
      return result.map(row =>
        typeof row === 'object' && row !== null
          ? convertBooleans(table, row as Record<string, unknown>)
          : row
      )
    }

    if (typeof result === 'object' && result !== null) {
      return convertBooleans(table, result as Record<string, unknown>)
    }

    return result
  }

  function clear(): void {
    booleanColumns.clear()
  }

  return { registerBooleanColumn, convertBooleans, postProcess, clear }
}

// Default shared instance for backward compatibility
const defaultProcessor = createSqliteBooleanProcessor()

export const registerBooleanColumn = defaultProcessor.registerBooleanColumn
export const convertBooleans = defaultProcessor.convertBooleans
export const sqlitePostProcess = defaultProcessor.postProcess
